import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type TipoProcesso = 'terceiros' | 'encomenda' | 'propria';

export interface DocumentoPayload {
  processo?: string;
  numeroNf: string;
  serie: string;
  // Chave de acesso do CT-e (44 digitos). So CTe - extraida do DACTE e
  // validada (DV) no front; backend grava em F1_CHVNFE. Omitida para NFSe.
  chaveAcesso?: string;
  emissao: string;       // ISO yyyy-mm-dd
  valor: number;
  vencimento?: string;   // ISO yyyy-mm-dd
  fin?: boolean;
  // Backend espera 'retencao' (toggle generico de retencao na UI).
  // Mantemos 'issRet' como compat opcional - sera removido apos a
  // janela de migracao.
  retencao?: boolean;
  issRet?: boolean;
  // Valores de retencao extraidos do DANFE PDF (via pdf-parser). Backend
  // usa em U_PnfNatFin pra escolher a F1_NATUREZ. Zeros = sem retencao.
  valorIr?: number;
  valorPis?: number;
  valorCofins?: number;
  valorCsll?: number;
  valorInss?: number;
  // Pedagio (CTe rodoviario): extraido do DACTE via cte-parser. Backend
  // deduz do total do produto (D1_TOTAL) e grava em F1_VALPEDG separado,
  // pra que a TES calcule a base de credito ICMS sem incluir pedagio.
  valorPedagio?: number;
  // Especie da nota fiscal (F1_ESPECIE). Default 'NFS' (NFSe). Quando o
  // parser detecta CTe pelo anexo, front envia 'CTE' pra Protheus aceitar
  // F1_VALPEDG e as TES de transporte. Pode ser omitido para usar default.
  especie?: 'NFS' | 'CTE';
  // TES escolhida pelo usuario no select de CTe (planilha REGRAS CTE).
  // Para NFSe, omitir - backend calcula via U_PnfTesNF.
  tipoOperacao?: string;
  // Simples Nacional do emitente (CTe). Determina o produto frete via
  // U_PnfPrdCte e o subconjunto de TES.
  simplesNacional?: boolean;
  // Tipo de processo resolvido no front via resolverProcesso (SW2.W2_ZTPIMPO).
  // Override per-doc do tipoProcesso do grupo.
  tipoProcesso?: TipoProcesso;
  // Fluxo fornecedor oculto: campos preenchidos manualmente pelo usuario
  // (sem processo). Backend usa quando ZOE flagar oculto.
  produto?: string;        // D1_COD (override do CNAE)
  contaContabil?: string;  // D1_CONTA
  centroCusto?: string;    // D1_CC
  bu?: string;             // D1_ITEMCTA (4 chars com padding zero)
}

export interface GrupoPayload {
  cnpjEmissor: string;
  fornecedorNome?: string;
  servicoCnae: string;
  tipoProcesso: TipoProcesso;
  // CNPJ da filial selecionada pelo usuario no front para gravar a NF.
  // Backend resolve via U_RetFil; se ausente/empty, cai pro header CNPJ.
  cnpjFilial?: string;
  documentos: DocumentoPayload[];
}

export interface ChaveNotaResp {
  filial: string;
  numero: string;
  serie: string;
  fornec: string;
  loja: string;
}

export interface ResultItem {
  numeroNf: string;
  ok: boolean;
  numero?: string;
  chave?: ChaveNotaResp;
  erro?: string;
  // Preenchido quando o backend escalonou a serie informada (a serie pedida
  // ja estava ocupada para o mesmo fornecedor + numero). A serie realmente
  // usada vem em chave.serie.
  aviso?: string;
}

export interface AdicionarResponse {
  success: boolean;
  totalOk: number;
  totalErr: number;
  results: ResultItem[];
}

export interface CnaeOption {
  cnae: string;
  descricao: string;
  produto: string;
}

interface CnaesResponse {
  success: boolean;
  data: CnaeOption[];
}

export interface RollbackReq {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
  anexosRecnos: number[];
}

export interface RollbackResp {
  success: boolean;
  sf1Deletada?: boolean;
  anexosDeletados?: string[];
  warnings?: string[];
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class NotasAdicionarService {
  private readonly base = 'portalnf/notas';

  constructor(private http: HttpClient) {}

  adicionar(grupos: GrupoPayload[]): Observable<AdicionarResponse> {
    return this.http.post<AdicionarResponse>(`${this.base}/adicionar`, { grupos });
  }

  // Desfaz a inclusao quando o upload de anexos falha.
  rollback(req: RollbackReq): Observable<RollbackResp> {
    return this.http.post<RollbackResp>(`${this.base}/rollback`, req);
  }

  cnaes(): Observable<CnaeOption[]> {
    return this.http.get<CnaesResponse>(`${this.base}/cnaes`)
      .pipe(map(r => r.data ?? []));
  }

  fornecedorPorCnpj(cnpj: string): Observable<FornecedorLookup> {
    const params = new HttpParams().set('cnpj', cnpj);
    return this.http.get<FornecedorLookup>(`${this.base}/fornecedor`, { params });
  }
}

export interface FornecedorLookup {
  success: boolean;
  exists: boolean;
  cod?: string;
  loja?: string;
  nome?: string;
  nreduz?: string;
  nomeSugerido?: string;
  fantasiaSugerido?: string;
  // .T. quando o fornecedor esta cadastrado em ZOE (oculto). Usado
  // pelo front pra liberar o requisito de "processo obrigatorio".
  oculto?: boolean;
}
